import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileDown, Shield, ExternalLink, Trash2, CheckCircle2,
  Route, AlertTriangle, MapPin, Gauge,
} from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

/**
 * Return Report panel — generates the official signed return PDF
 * combining receipt + checklist + AI inspection + extras + signature.
 */
export default function ReturnReport({ rental, onSigned }) {
  const { t, lang, fmtCurrency, fmtNumber } = useT();
  const rentalId = rental?.id;
  const [reportLang, setReportLang] = useState(rental?.contract_lang || lang || "en");
  const [storedReports, setStoredReports] = useState([]);
  const [closing, setClosing] = useState(null);

  const isSigned = !!rental?.customer_return_signature;
  const extras = rental?.extra_charges || rental?.tolls || [];
  const extrasTotal = extras.reduce((a, e) => a + Number(e.amount || 0), 0);
  const deposit = Number(rental?.deposit || 0);
  const balance = deposit - extrasTotal;

  const loadStored = async () => {
    try {
      const r = await api.get("/files", {
        params: { owner_type: "rental_return_report", owner_id: rentalId },
      });
      setStoredReports(r.data || []);
    } catch (e) {
      // silent
    }
  };
  const loadClosing = async () => {
    try {
      const r = await api.get(`/rentals/${rentalId}/closing-summary`);
      setClosing(r.data);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => {
    if (rentalId) {
      loadStored();
      loadClosing();
    }
  }, [rentalId, rental?.customer_return_signature]);

  const previewPdf = async () => {
    try {
      const r = await api.get(`/rentals/${rentalId}/return-report.pdf`, {
        params: { lang: reportLang },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        // Fallback for popup blockers — force download
        const a = document.createElement("a");
        a.href = url;
        a.download = `return-report-${rentalId.slice(0, 8)}.pdf`;
        a.click();
      }
      // Revoke after a delay so the tab can load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("returnReport.previewError"));
    }
  };

  const removeSignature = async () => {
    if (!window.confirm(t("signature.confirmRemove"))) return;
    try {
      await api.delete(`/rentals/${rentalId}/return-signature`);
      toast.success(t("signature.removed"));
      onSigned?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openStored = async (f) => {
    try {
      const r = await api.get(`/files/${f.id}/raw`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: f.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = f.original_filename || `return-report-${rentalId.slice(0, 8)}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="return-report-panel">
      {/* Hero box */}
      <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="bg-violet-600 text-white p-2"><Shield className="w-5 h-5" /></div>
            <div>
              <div className="overline text-violet-700">{t("returnReport.title")}</div>
              <p className="text-xs text-zinc-700 max-w-prose mt-0.5">{t("returnReport.subtitle")}</p>
            </div>
          </div>
          <div className="inline-flex items-stretch border border-violet-300 bg-white" data-testid="return-report-lang-toggle">
            {["pt", "en", "es"].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setReportLang(code)}
                className={`px-3 py-1.5 text-xs font-mono uppercase ${
                  reportLang === code ? "bg-violet-600 text-white" : "text-zinc-700 hover:bg-violet-50"
                }`}
                data-testid={`return-lang-${code}`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Settlement summary */}
      <div className="grid grid-cols-3 gap-0 border-l border-t border-zinc-200" data-testid="return-settlement">
        <Cell label={t("returnReport.deposit")} value={fmtCurrency(deposit)} />
        <Cell label={t("returnReport.charges")} value={fmtCurrency(extrasTotal)} valueClass="text-rose-700" />
        <Cell
          label={balance >= 0 ? t("returnReport.refund") : t("returnReport.due")}
          value={fmtCurrency(Math.abs(balance))}
          valueClass={balance >= 0 ? "text-green-700" : "text-rose-700"}
          big
        />
      </div>

      {/* Closing breakdown (mileage + tolls + tickets) */}
      {closing && (
        <ClosingBreakdown closing={closing} t={t} fmtCurrency={fmtCurrency} fmtNumber={fmtNumber} />
      )}

      {/* Preview button */}
      <Button onClick={previewPdf} variant="outline" className="rounded-none w-full" data-testid="preview-return-report-button">
        <FileDown className="w-4 h-4 mr-2" /> {t("returnReport.previewPdf")}
      </Button>

      {/* Signature pad */}
      <div className="border border-zinc-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">{t("returnReport.customerSignature")}</div>
          {isSigned && (
            <button
              onClick={removeSignature}
              className="text-xs text-rose-600 hover:underline flex items-center gap-1"
              data-testid="remove-return-signature-button"
            >
              <Trash2 className="w-3 h-3" /> {t("signature.remove")}
            </button>
          )}
        </div>
        <p className="text-xs text-zinc-600">{t("returnReport.signatureHint")}</p>

        {isSigned ? (
          <div className="bg-green-50 border border-green-300 p-3 flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
            <span className="text-green-800">
              {t("signature.signedOn")} <strong>{new Date(rental.return_signed_at).toLocaleString()}</strong>
            </span>
          </div>
        ) : (
          <SignaturePad
            rentalId={rentalId}
            endpoint={`/rentals/${rentalId}/return-signature`}
            savedMessage={t("returnReport.savedAndStored")}
            showInitials={false}
            onSaved={() => {
              onSigned?.();
              loadStored();
            }}
          />
        )}
      </div>

      {/* Stored versions */}
      {storedReports.length > 0 && (
        <div className="border border-zinc-200 bg-white p-3 space-y-2" data-testid="return-report-versions">
          <div className="overline">{t("returnReport.storedTitle")}</div>
          {storedReports.map((f) => (
            <button
              key={f.id}
              onClick={() => openStored(f)}
              className="w-full flex items-center justify-between text-left text-xs border border-zinc-200 px-3 py-2 hover:border-violet-400 hover:bg-violet-50"
            >
              <span className="font-mono truncate">{f.original_filename}</span>
              <span className="text-zinc-500 flex items-center gap-1">
                {new Date(f.created_at).toLocaleDateString()} <ExternalLink className="w-3 h-3" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, valueClass = "", big = false }) {
  return (
    <div className="border-r border-b border-zinc-200 p-3">
      <div className="overline text-[10px] text-zinc-500">{label}</div>
      <div className={`font-mono font-bold ${big ? "text-2xl" : "text-lg"} ${valueClass}`}>{value}</div>
    </div>
  );
}

function ClosingBreakdown({ closing, t, fmtCurrency, fmtNumber }) {
  const mileage = closing.mileage || {};
  const tolls = closing.tolls || [];
  const tickets = closing.tickets || [];
  const car = closing.car || {};
  return (
    <div className="border border-zinc-200 bg-white p-4 space-y-4" data-testid="closing-breakdown">
      <div className="overline">{t("returnReport.closingTitle")}</div>

      {/* Mileage cells */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <MiniCell icon={<Gauge className="w-3 h-3" />} label={t("returnReport.milesAtPickup")} value={mileage.at_pickup != null ? fmtNumber(mileage.at_pickup) : "—"} />
        <MiniCell icon={<Gauge className="w-3 h-3" />} label={t("returnReport.milesAtReturn")} value={mileage.at_return != null ? fmtNumber(mileage.at_return) : "—"} />
        <MiniCell icon={<Route className="w-3 h-3" />} label={t("returnReport.milesDriven")} value={mileage.driven != null ? fmtNumber(mileage.driven) : "—"} valueClass="text-[#F5A623]" />
        <MiniCell icon={<MapPin className="w-3 h-3" />} label={t("returnReport.lastLocation")} value={car.last_lat && car.last_lng ? `${car.last_lat.toFixed(3)}, ${car.last_lng.toFixed(3)}` : "—"} mono small />
      </div>

      {/* Tolls */}
      <div>
        <div className="text-xs font-semibold flex items-center gap-1.5 mb-1.5">
          <Route className="w-3.5 h-3.5 text-zinc-500" /> {t("returnReport.tollsTitle")}
          <span className="ml-auto font-mono text-zinc-700">{fmtCurrency(closing.tolls_total || 0)}</span>
        </div>
        {tolls.length === 0 ? (
          <div className="text-[11px] text-zinc-500 italic px-2">{t("returnReport.noTolls")}</div>
        ) : (
          <div className="border border-zinc-200 max-h-44 overflow-y-auto">
            {tolls.map((tl) => (
              <div key={tl.id} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-zinc-100 last:border-0">
                <div className="font-mono text-zinc-500 w-28 shrink-0">{(tl.toll_datetime || "").slice(0, 16).replace("T", " ")}</div>
                <div className="flex-1 truncate px-2">{tl.location || "—"}</div>
                <div className="font-mono font-semibold">{fmtCurrency(tl.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tickets */}
      <div>
        <div className="text-xs font-semibold flex items-center gap-1.5 mb-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> {t("returnReport.ticketsTitle")}
          <span className="ml-auto font-mono text-rose-700">{fmtCurrency(closing.tickets_total || 0)}</span>
        </div>
        {tickets.length === 0 ? (
          <div className="text-[11px] text-zinc-500 italic px-2">{t("returnReport.noTickets")}</div>
        ) : (
          <div className="border border-zinc-200 max-h-44 overflow-y-auto">
            {tickets.map((tk) => (
              <div key={tk.id} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-zinc-100 last:border-0">
                <div className="font-mono text-zinc-500 w-28 shrink-0">{(tk.issue_datetime || "").slice(0, 16).replace("T", " ")}</div>
                <div className="flex-1 truncate px-2">
                  <span className="font-mono">{tk.citation_number}</span>
                  <span className="text-zinc-500"> · {(tk.ticket_type || "").replace("_", " ")}</span>
                </div>
                <div className="font-mono font-semibold text-rose-700">{fmtCurrency(tk.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniCell({ icon, label, value, valueClass = "", mono = false, small = false }) {
  return (
    <div className="border-r border-b border-zinc-200 p-2.5">
      <div className="overline text-[9px] text-zinc-500 flex items-center gap-1">{icon} {label}</div>
      <div className={`font-mono font-bold ${small ? "text-[11px]" : "text-base"} ${valueClass} ${mono ? "tracking-tight" : ""}`}>{value}</div>
    </div>
  );
}
